import { useSelector } from 'react-redux';
import type { SupplierAssessment, UserOrganisation } from '../../types/types';
import { useSupplierAssessments } from '../../hooks/useSupplierAssessments';
import {
  Card,
  Row,
  Col,
  Spinner,
  Button,
  Container,
  Badge,
  Alert,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getAssessmentTotalScore, getBarColor } from '../../util/util';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import { useRef } from 'react';
import ErrorComponent from '../common/Error';

export default function SupplierDashboard() {
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const navigate = useNavigate();

  const nextActionsRef = useRef<HTMLHeadingElement>(null);

  const { assessments, isLoading, error } = useSupplierAssessments(
    selectedOrganisation?.organisation?.id,
  );

  if (isLoading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );

  if (error) {
    return <ErrorComponent message={error} />;
  }

  // ─── Derivations ──────────────────────────────────────────────
  const assigned = assessments.filter((a) => !a.is_self_assessment);

  const actionRequired = assessments.filter((a) => a.status !== 'completed');

  const completed = assessments.filter((a) => a.status === 'completed');

  // const selfAssessments = assessments.filter((a) => a.is_self_assessment);
  // const published = selfAssessments.filter((a) => a.is_report_published);

  const buyers = assigned
    .map((a) => a.assigning_organisation_name)
    .filter((value, index, self) => self.indexOf(value) === index); // Unique

  // Sort next actions by created date
  const nextActions = [...actionRequired].sort((a, b) => {
    const da = a.created_at ? new Date(a.created_at).getTime() : 0;
    const db = b.created_at ? new Date(b.created_at).getTime() : 0;
    return da - db;
  });

  /**
   * {
  "assessment_id": "1ddfc41d-1838-4dd6-b93a-184b411efc83",
  "assigning_organisation_name": "Supplier Thirty",
  "created_at": "2026-02-16T21:00:11.745+00:00",
  "is_report_published": false,
  "is_self_assessment": true,
  "max_score": null,
  "metadata": null,
  "published_report": null,
  "started_at": "2026-02-16T21:00:11.771762+00:00",
  "status": "created",
  "submitted_at": null,
  "template_id": "313bc74b-1b6e-4320-b98c-70934840c681",
  "template_status": "published",
  "template_title": "Supplier ESG Report",
  "template_version": 1,
  "template_version_number": 1,
  "total_score": 0,
  "updated_at": "2026-02-16T21:00:11.745+00:00"
}
   */

  const statusBadge = (assessment: SupplierAssessment) => {
    if (assessment.status === 'created') {
      return <Badge bg="secondary">Not started</Badge>;
    } else if (assessment.status === 'started') {
      return <Badge bg="warning">Started</Badge>;
    } else if (assessment.status === 'completed') {
      return <Badge bg="success">Completed</Badge>;
    }

    return <Badge bg="success">{assessment.status}</Badge>;
  };

  // ─── UI ───────────────────────────────────────────────────────
  return (
    <Container fluid>
      <h3 className="mb-4">
        Welcome, {selectedOrganisation?.organisation?.name}
      </h3>

      {/* Summary */}
      <Row className="mb-4">
        <Col md={6} lg={3}>
          {/* <Card className="shadow-sm mb-3 bg-light border border-3 border-danger-subtle"> */}
          <Card className="mb-3 border-top-0 border-end-0 border-bottom-0 border-3 border-danger shadow-sm bg-light">
            <Card.Body>
              <Card.Title>
                {actionRequired.length > 0 ? (
                  <i className="bi bi-exclamation-circle-fill text-danger me-2"></i>
                ) : (
                  <i className="bi bi-check-circle-fill text-danger me-2"></i>
                )}
                Action required
              </Card.Title>
              <div className="d-flex justify-content-between align-items-center">
                <span className="fs-1">{actionRequired.length}</span>
                {actionRequired.length > 0 && (
                  <Button
                    variant="link"
                    onClick={() => {
                      const assesmentLink = nextActions[0]?.is_self_assessment
                        ? '/assessments?tab=selfAssessments'
                        : '/assessments?tab=assigned';
                      navigate(assesmentLink);
                    }}
                  >
                    View all →
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="mb-3 border-top-0 border-end-0 border-bottom-0 border-3 border-warning shadow-sm bg-light">
            <Card.Body>
              <Card.Title>
                <i className="bi bi-file-earmark-check-fill text-warning me-2"></i>{' '}
                Assigned
              </Card.Title>
              <span className="fs-1">{assigned.length}</span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="mb-3 border-top-0 border-end-0 border-bottom-0 border-3 border-success shadow-sm bg-light">
            <Card.Body>
              <Card.Title>
                <i className="bi bi-check-circle-fill text-success me-2"></i>
                Completed
              </Card.Title>
              <span className="fs-1">{completed.length}</span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} lg={3}>
          <Card className="mb-3 border-top-0 border-end-0 border-bottom-0 border-3 border-info shadow-sm bg-light">
            <Card.Body>
              <Card.Title>
                <i className="bi bi-file-earmark-text-fill text-info me-2"></i>
                Buyers
              </Card.Title>
              <span className="fs-1">{buyers.length}</span>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Alert variant="info" className="d-flex align-items-center">
            <i className="bi bi-info-circle-fill me-2"></i>
            <div>
              Build your compliance profile to showcase your efforts to buyers
              and get matched with more opportunities.{' '}
              <Alert.Link href="/my-profile">Create your profile →</Alert.Link>
            </div>
          </Alert>
        </Col>
      </Row>

      {/* Next actions */}
      {nextActions.length > 0 && (
        <>
          <h5 className="mb-3" ref={nextActionsRef}>
            Your next actions
          </h5>
          <Row xs={1} sm={2} md={3} lg={4} className="g-4 mb-4">
            {nextActions.slice(0, 4).map((a) => (
              <Col key={a.assessment_id}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <Card.Title>{a.template_title}</Card.Title>
                    <Card.Subtitle className="mb-2 text-muted">
                      {a.is_self_assessment
                        ? 'Self-assessment'
                        : `Assigned by ${a.assigning_organisation_name}`}
                    </Card.Subtitle>

                    {a.status && <div className="mb-2">{statusBadge(a)}</div>}

                    <Button
                      size="sm"
                      onClick={() => navigate(`/assessment/${a.assessment_id}`)}
                    >
                      {a.status === 'created' ? 'Start' : 'Continue'}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {nextActions.length > 4 && (
            <Button variant="link" onClick={() => navigate('/assessments')}>
              View all assessments →
            </Button>
          )}
        </>
      )}

      {/* Completed overview */}
      {completed.length > 0 && (
        <>
          <h5 className="mt-5 mb-3">Recently completed</h5>
          <Row xs={1} sm={2} md={3} lg={4} className="g-4">
            {completed.slice(0, 4).map((a) => (
              <Col key={a.assessment_id}>
                <Card className="h-100">
                  <Card.Body>
                    <h6>{a.template_title}</h6>
                    <h6 className="text-muted mb-3">
                      {a.is_self_assessment
                        ? 'Self-assessment'
                        : `Assigned by ${a.assigning_organisation_name}`}
                    </h6>
                    <div className="d-flex justify-content-center mb-3 flex-column align-items-center">
                      <div style={{ maxWidth: '160px', maxHeight: '160px' }}>
                        <CircularProgressbar
                          value={getAssessmentTotalScore(
                            a.total_score,
                            a.max_score,
                          )}
                          text={`${getAssessmentTotalScore(
                            a.total_score,
                            a.max_score,
                          )}%`}
                          styles={buildStyles({
                            pathColor: getBarColor(
                              getAssessmentTotalScore(
                                a.total_score,
                                a.max_score,
                              ),
                            ),
                            trailColor: '#E5E7EB',
                            textColor: '#111827',
                            textSize: '16px',
                          })}
                        />
                      </div>
                      <div className="mt-2 fs-5">Total Score</div>
                    </div>
                  </Card.Body>
                  <Card.Footer className="d-flex justify-content-end">
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() =>
                        navigate(`/assessmentResult/${a.assessment_id}`)
                      }
                    >
                      View report
                    </Button>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* Empty state */}
      {assessments.length === 0 && (
        <div
          className="text-center my-5"
          style={{
            border: '4px dashed #ccc',
            borderRadius: '10px',
            padding: '40px',
          }}
        >
          <h5>No assessments yet</h5>
          <p className="text-muted">
            Start a self-assessment or ask buyers to assign one to you.
          </p>
          <Button onClick={() => navigate('/self-assessments')}>
            Start self-assessment
          </Button>
        </div>
      )}
    </Container>
  );
}
